import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { ReactNode, memo } from 'react'
import { RiveSpinner } from '../ui/RiveSpinner'

interface OAuthButtonProps {
  onClick: () => void
  disabled: boolean
  isLoading: boolean
  icon: ReactNode
  children: ReactNode
}

export const OAuthButton = memo(function OAuthButton({
  onClick,
  disabled,
  isLoading,
  icon,
  children,
}: OAuthButtonProps) {
  return (
    <motion.div
      className={cn(
        'rounded-[18px] p-[2px] transition-all duration-300',
        disabled ? 'bg-gray-300' : 'bg-gradient-to-r from-pink-300 to-cyan-300'
      )}
      whileHover={
        !disabled
          ? {
              scale: 1.02,
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.25)',
            }
          : undefined
      }
      whileTap={!disabled ? { scale: 0.98 } : undefined}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      <motion.button
        className={cn(
          `relative inline-flex w-full items-center justify-center gap-2`,
          `px-4 py-3 text-sm font-medium shadow-sm`,
          `focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2`,
          `rounded-[16px] transition-all duration-200`,
          disabled
            ? 'cursor-not-allowed bg-gray-100 text-gray-400 focus-visible:ring-gray-300'
            : 'bg-[#3f3f46] text-white hover:bg-[#2f2f34] focus-visible:ring-[#6b7280]'
        )}
        onClick={onClick}
        disabled={disabled}
        whileHover={
          !disabled
            ? {
                backgroundColor: '#2f2f34',
                boxShadow: '0 0 20px rgba(79, 172, 254, 0.3)',
              }
            : undefined
        }
        transition={{ duration: 0.2 }}
      >
        <motion.div
          animate={isLoading ? { rotate: 360 } : { rotate: 0 }}
          transition={
            isLoading ? { duration: 2, repeat: Infinity, ease: 'linear' } : { duration: 0.2 }
          }
        >
          {isLoading ? <RiveSpinner size={24} /> : icon}
        </motion.div>
        <motion.span
          className="text-base font-medium"
          initial={false}
          animate={isLoading ? { opacity: 0.7 } : { opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.span>
      </motion.button>
    </motion.div>
  )
})
